import React, { useState } from 'react';
import { Box, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, Paper, Grid, Chip } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentAccessToken } from '../features/authentication/authSlice';
import { selectPersonByName } from '../api/peopleApiSlice';
import handleUpdate from './AdminPage/adminUtils/handleUpdate';
import HelpIconWithTooltip from '../components/HelpIconWithTooltip';

const ProfilePage = ({ documentTitle }) => {
    const user = useSelector(selectCurrentUser);
    const person = useSelector(state => selectPersonByName(user?.name)(state)); // Fetch person details by user name
    const accessToken = useSelector(selectCurrentAccessToken);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handlePasswordChange = () => {
        if (newPassword !== confirmPassword) {
            setPasswordError('Salasanat eivät ole samat!');
            return;
        }
        if (newPassword.length < 6) {
            setPasswordError('Salasanan pitää olla vähintään kuusi merkkiä pitkä.');
            return;
        }

        // Prepare the password update object
        const updatedUser = {
            id: user.userId,
            password: newPassword
        };

        // Call the handleUpdate function to update the password
        handleUpdate(
            'users', // Endpoint for updating user information
            accessToken,
            updatedUser,
            () => {}, // We don't need to update a list for password change
            (message) => {
                // Success callback
                setSuccessMessage('Salasanan päivitys onnistui!');
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);
            },
            (errorMessage) => {
                // Error callback
                setPasswordError(errorMessage);
            },
            null, // Assuming no logs needed for password change
            user.name // Assuming you want to log the name of the user changing the password
        );

        // Reset the password fields and close the modal
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setOpenPasswordModal(false);
    };

    // If person data is not available
    if (!person) {
        return (
            <div>
                <Paper sx={{ padding: 3, maxWidth: 900, margin: 'auto', mt: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Käyttäjän henkilötietoja ei löytynyt
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                    Henkilötietojasi ei löytynyt, voi olla mahdollista ettei käyttäjänimelläsi ole vielä luotu henkilöä. Henkilötietojen löytymiseksi on oltava olemassa myös samanniminen henkilö tietokannassa kuin käyttäjänimesi. Voit kuitenkin vaihtaa käyttäjäsi salasanan.
                    </Typography>
                    {/* Success Message */}
                    {successMessage && (
                        <Box sx={{ marginTop: 2, padding: 1, backgroundColor: '#4caf50', color: 'white', borderRadius: 1 }}>
                            <Typography variant="body1">{successMessage}</Typography>
                        </Box>
                    )}
                    <Grid container spacing={2} sx={{ marginTop: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Ota yhteyttä
                            </Typography>
                            <TextField
                                fullWidth
                                label="Email"
                                value={'Ei tietoa löydettävissä'}
                                variant="outlined"
                                disabled
                                sx={{ marginTop: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Puhelinnumero"
                                value={'Ei tietoa löydettävissä'}
                                variant="outlined"
                                disabled
                                sx={{ marginTop: 2 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Lisätiedot
                            </Typography>
                            <TextField
                                fullWidth
                                label="Titteli"
                                value={'Ei tietoa löydettävissä'}
                                variant="outlined"
                                disabled
                                sx={{ marginTop: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Toinen puhelinnumero"
                                value={'Ei tietoa löydettävissä'}
                                variant="outlined"
                                disabled
                                sx={{ marginTop: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Liity lähettäjäksi"
                                value={'Ei tietoa löydettävissä'}
                                variant="outlined"
                                disabled
                                sx={{ marginTop: 2 }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ marginTop: 3 }}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Yhteystiedot
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Chip label={'Ei tietoa löydettävissä'} color="primary" variant="outlined" />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
                <Box display="flex" justifyContent="center" sx={{ marginTop: 4 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ width: 200 }}
                        onClick={() => setOpenPasswordModal(true)}
                    >
                        Vaihda salasana
                    </Button>
                </Box>
                <Dialog open={openPasswordModal} onClose={() => setOpenPasswordModal(false)}>
                    <DialogTitle>Vaihda salasana</DialogTitle>
                    <DialogContent>
                        <Box display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                            <TextField
                                label="Uusi salasana"
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                            <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                                sx={{ marginLeft: 1 }}
                            >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </Box>

                        {/* Confirm Password Field */}
                        <Box display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                            <TextField
                                label="Varmista uusi salasana"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                            <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                                sx={{ marginLeft: 1 }}
                            >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </Box>

                        {passwordError && (
                            <Typography color="error" sx={{ marginTop: 2 }}>
                                {passwordError}
                            </Typography>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setOpenPasswordModal(false)} color="secondary">
                            Peru
                        </Button>
                        <Button onClick={handlePasswordChange} color="primary">
                            Päivitä
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>

        );
    }

    // Profile Content (if person data exists)
    return (
        <div>
            <Paper sx={{ padding: 3, maxWidth: 900, margin: 'auto', mt: 4 }}>
                <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                    <Box
                        sx={{
                            width: 220,
                            height: 220,
                            overflow: 'hidden',
                            border: '4px solid #ddd',
                            mb: { xs: 3, sm: 0 },
                        }}
                    >
                        <img src={person?.henkilokuva?.src || ''} alt={`${user.name}'s Avatar`} style={{ width: '100%', height: '100%' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {user.name}
                        </Typography>
                        <Typography variant="body1" color="textSecondary" sx={{ marginTop: 1 }}>
                            {person?.tyonkuva || 'Työn kuvausta ei ole saatavilla.'}
                        </Typography>
                    </Box>
                </Box>
                {/* Success Message */}
                {successMessage && (
                    <Box sx={{ marginTop: 2, padding: 1, backgroundColor: '#4caf50', color: 'white', borderRadius: 1 }}>
                        <Typography variant="body1">{successMessage}</Typography>
                    </Box>
                )}

                <Grid container spacing={2} sx={{ marginTop: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Ota yhteyttä
                        </Typography>
                        <TextField
                            fullWidth
                            label="Email"
                            value={person?.email || 'Ei tietoa löydettävissä'}
                            variant="outlined"
                            disabled
                            sx={{ marginTop: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Puhelinnumero"
                            value={person?.puhelinnumero || 'Ei tietoa löydettävissä'}
                            variant="outlined"
                            disabled
                            sx={{ marginTop: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Lisätiedot
                        </Typography>
                        <TextField
                            fullWidth
                            label="Titteli"
                            value={person?.titteli || 'Ei titteliä'}
                            variant="outlined"
                            disabled
                            sx={{ marginTop: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Toinen puhelinnumero"
                            value={person?.puhelinnumero2 || 'Ei saatavilla'}
                            variant="outlined"
                            disabled
                            sx={{ marginTop: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Liity lähettäjäksi"
                            value={person?.liity_lahettajaksi || 'Ei saatavilla'}
                            variant="outlined"
                            disabled
                            sx={{ marginTop: 2 }}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ marginTop: 3 }}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Kontaktit <HelpIconWithTooltip tooltipFontSize="16px" text="Kontaktisi vaikuttavat siihen, missä listoissa yhteystietosi näytetään SRO:n sivuilla."/>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {person?.yhteystiedot?.length > 0
                                ? person?.yhteystiedot.map((area, index) => (
                                    <Chip key={index} label={area} color="primary" variant="outlined" />
                                ))
                                : <Chip label="Ei tietoa löydettävissä" color="primary" variant="outlined" />}
                        </Box>
                    </Grid>
                </Grid>

                {/* Password Change Button */}
                <Box display="flex" justifyContent="center" sx={{ marginTop: 4 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ width: 200 }}
                        onClick={() => setOpenPasswordModal(true)}
                    >
                        Vaihda salasana
                    </Button>
                </Box>
            </Paper>

            {/* Password Change Modal */}
            <Dialog open={openPasswordModal} onClose={() => setOpenPasswordModal(false)}>
                <DialogTitle>Vaihda salasana</DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                        <TextField
                            label="Uusi salasana"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            variant="outlined"
                            fullWidth
                        />
                        <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            sx={{ marginLeft: 1 }}
                        >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </Box>

                    {/* Confirm Password Field */}
                    <Box display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                        <TextField
                            label="Varmista uusi salasana"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            variant="outlined"
                            fullWidth
                        />
                        <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            sx={{ marginLeft: 1 }}
                        >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </Box>

                    {passwordError && (
                        <Typography color="error" sx={{ marginTop: 2 }}>
                            {passwordError}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenPasswordModal(false)} color="secondary">
                        Peru
                    </Button>
                    <Button onClick={handlePasswordChange} color="primary">
                        Päivitä
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ProfilePage;
